import { CONTROL_LIT } from "@majourneys/shared/ui";
import { useState } from "react";
import { useTranslation, Trans } from "react-i18next";
import { MailWarning, X } from "lucide-react";
import { useSession } from "../../lib/auth";
import { authApi } from "../../features/auth/api/auth.api";
import { panelStyle } from "../ui/card-frame";

const NAVY = "#110E39";
const GOLD_DARK = "#8A6425";
const GREEN = "#1A7A44";
const RED = "#9B2C2C";
const MUTED = "#5a5d77";
/** The sheet tone under a gold wash, as one layer: the frame paints its fill in one slot. */
const GOLD_WASH = "linear-gradient(180deg, #f8eedf 0%, #f2e7d9 100%)";
/** A control at rest: white with one navy hairline, never the band. */
const CONTROL_EDGE = "rgba(17,14,57,0.16)";
/** A control at rest — the one definition, in `@majourneys/shared/ui`. */
const CONTROL_FILL = CONTROL_LIT;

/** The gold strip above the bar, in the same frame, for a signed-in guest whose email is not verified. */
export function EmailVerificationBanner() {
  const { t } = useTranslation();
  const { data: session } = useSession();
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [dismissed, setDismissed] = useState(false);

  if (!session?.user) return null;
  if (session.user.emailVerified) return null;
  if (dismissed) return null;

  const email = session.user.email;

  async function handleResend() {
    if (!email) return;
    setError(null);
    setSent(false);
    setLoading(true);
    try {
      const result = await authApi.resendVerificationEmail({ email });
      if (result && "error" in result && result.error) {
        setError(result.error.message ?? t("email_verification.banner_message"));
        return;
      }
      setSent(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : t("email_verification.banner_message"));
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="w-full px-3 pt-2 md:px-7">
      <div
        className="mx-auto flex w-full max-w-[1440px] flex-wrap items-center gap-x-3 gap-y-2 px-4 py-2.5"
        style={panelStyle(16, GOLD_WASH)}
      >
        <MailWarning size={15} strokeWidth={2.2} className="shrink-0" style={{ color: GOLD_DARK }} aria-hidden />
        <p className="min-w-[200px] flex-1 text-[13px] leading-snug" style={{ color: NAVY }}>
          <Trans
            i18nKey="email_verification.banner_message_with_email"
            values={{ email }}
            components={{ strong: <span className="font-semibold" /> }}
          />
        </p>
        <span className="ms-auto flex flex-wrap items-center gap-2">
          {sent ? (
            <span role="status" className="text-[12px] font-semibold" style={{ color: GREEN }}>
              {t("email_verification.resent")}
            </span>
          ) : null}
          {error ? (
            <span role="alert" className="text-[12px] font-semibold" style={{ color: RED }}>
              {error}
            </span>
          ) : null}
          <button
            type="button"
            disabled={loading || sent}
            onClick={handleResend}
            className="mj-press inline-flex min-h-8 items-center whitespace-nowrap rounded-full px-4 py-1.5 text-[12.5px] font-semibold disabled:opacity-35"
            style={{ background: CONTROL_FILL, boxShadow: `0 0 0 1px ${CONTROL_EDGE}`, color: NAVY }}
          >
            {loading ? t("email_verification.resending") : sent ? t("email_verification.resent") : t("email_verification.resend")}
          </button>
          <button
            type="button"
            onClick={() => setDismissed(true)}
            aria-label={t("common.dismiss", { defaultValue: "Dismiss" })}
            className="mj-press grid h-6 w-6 shrink-0 place-items-center rounded-full hover:bg-black/5"
            style={{ color: MUTED }}
          >
            <X size={14} strokeWidth={2.4} aria-hidden />
          </button>
        </span>
      </div>
    </div>
  );
}
